import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { CheckCircle, XCircle } from "@phosphor-icons/react";

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

export default function SetupUsername() {
  const { user, fetchMe } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [avail, setAvail] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.username_set) nav("/app/home", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    const u = username.trim().toLowerCase();
    if (!u) { setAvail(null); return; }
    if (!USERNAME_RE.test(u)) { setAvail("invalid"); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/username/check?username=${encodeURIComponent(u)}`);
        setAvail(data.available);
      } catch { setAvail(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [username]);

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/username", { username: username.toLowerCase() });
      await fetchMe();
      nav("/app/home", { replace: true });
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not set username");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background grid place-items-center px-6 py-10 relative">
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="relative w-full max-w-md card-sharp p-8 fade-in">
        <h1 className="font-display text-3xl">Pick your handle.</h1>
        <p className="text-sm text-muted-foreground mt-2">
          One last step — choose a unique username so your network can find and tag you.
        </p>

        <div className="mt-6">
          <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-1">Username</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
            <input
              data-testid="setup-username-input"
              className="input-sharp pl-7"
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase())}
              placeholder="your_handle"
              minLength={3}
              maxLength={20}
              autoFocus
            />
            {avail === true && (
              <span data-testid="setup-username-available" className="absolute right-2 top-1/2 -translate-y-1/2 text-[#F2DC8C] flex items-center gap-1 text-[10px] uppercase tracking-widest"><CheckCircle size={14} weight="fill"/> Free</span>
            )}
            {avail === false && (
              <span data-testid="setup-username-taken" className="absolute right-2 top-1/2 -translate-y-1/2 text-[#D4AF37] flex items-center gap-1 text-[10px] uppercase tracking-widest"><XCircle size={14} weight="fill"/> Taken</span>
            )}
          </div>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">3-20 chars · a-z · 0-9 · _</p>
        </div>

        {err && <div data-testid="setup-username-error" className="text-sm text-[#D4AF37] mt-3">{err}</div>}

        <button
          data-testid="setup-username-submit"
          disabled={busy || avail !== true}
          onClick={save}
          className="btn-neon w-full justify-center mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? "Saving…" : "Claim Handle"}
        </button>
      </div>
    </div>
  );
}
